import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { Button } from "../../components/ui/button";
import { formatDateTime } from "../../lib/format";
import { useCan, useForexAdmin } from "./api";
import { AccountLink, BASE, Fig, Panel, QueryState, Stat, Unavail, linkCls, mono, muted, useOpen } from "./parts";
import {
  CONNECTION_COLUMNS,
  MASTER_COLUMNS,
  chargeColumns,
  chargeRow,
  connectionRow,
  masterRow,
} from "./rows";
import { TransferDrawer, transferRow } from "./MtTransfers";
import { ChargeDrawer } from "./Settlements";
import { CreateMasterDrawer } from "./Masters";

const TRANSFER_COLUMNS = [
  { label: "Created" }, { label: "Ref" }, { label: "State" }, { label: "Username" },
  { label: "From login" }, { label: "To login" }, { label: "Amount", align: "right" },
  { label: "Debit" }, { label: "Credit" }, { label: "Refund" },
];

const ACCOUNT_COLUMNS = [
  { label: "Login" }, { label: "Kind" }, { label: "Tier" }, { label: "Purpose" }, { label: "Group" },
  { label: "Currency" }, { label: "Balance", align: "right" }, { label: "Credit", align: "right" },
  { label: "Equity", align: "right" }, { label: "Margin", align: "right" },
  { label: "Floating", align: "right" }, { label: "Opened" },
];

const accountRow = (r) => (
  <tr key={r.id} className="hover:bg-white/[0.02]">
    <Td className={mono}><AccountLink id={r.id}>{r.login}</AccountLink></Td>
    <Td className="text-xs">{r.kind}</Td>
    <Td className="text-xs">{r.tier}</Td>
    <Td className="whitespace-nowrap text-xs">{r.purpose}</Td>
    <Td className="whitespace-nowrap text-xs">{r.group}</Td>
    <Td className="text-xs">{r.currency}</Td>
    <Td align="right"><Fig v={r.balance} /></Td>
    <Td align="right"><Fig v={r.credit} /></Td>
    <Td align="right"><Fig v={r.equity} /></Td>
    <Td align="right"><Fig v={r.margin} /></Td>
    <Td align="right"><Fig v={r.floatingProfit} /></Td>
    <Td className={muted}>{formatDateTime(r.createdAt)}</Td>
  </tr>
);

// null name/email = the dashboard did not answer within 3s.
const orUnavail = (v) => v ?? <Unavail />;

export default function UserDetail() {
  const { username } = useParams();
  const q = useForexAdmin(`/users/admin/${encodeURIComponent(username)}`);
  const [transfer, setTransfer] = useOpen("transfer");
  const [charge, setCharge] = useOpen("charge");
  const [creating, setCreating] = useState(false);
  const can = useCan();
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;

  return (
    <QueueLayout
      eyebrow="Forex Admin · User"
      title={username}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <div className="flex items-center justify-between">
        <Link to={`${BASE}/users`} className={`text-xs text-muted-foreground ${linkCls}`}>← All users</Link>
        {can("masters.open") && (
          <Button size="sm" onClick={() => setCreating(true)}>+ Create master for {username}</Button>
        )}
      </div>
      <QueryState q={q}>
        {(u) => (
          <div className="space-y-10">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat label="First name"><span className="text-sm">{orUnavail(u.firstName)}</span></Stat>
              <Stat label="Last name"><span className="text-sm">{orUnavail(u.lastName)}</span></Stat>
              <Stat label="Email"><span className="text-sm">{orUnavail(u.email)}</span></Stat>
            </div>
            <Panel title={`Accounts (${u.accounts.length})`}>
              <QueueTable columns={ACCOUNT_COLUMNS} rows={u.accounts} renderRow={accountRow} />
            </Panel>
            <Panel title={`Masters owned (${u.masters.length})`}>
              <QueueTable columns={MASTER_COLUMNS} rows={u.masters} renderRow={masterRow} />
            </Panel>
            <Panel title={`Connections as copier (${u.connections.length})`}>
              <QueueTable columns={CONNECTION_COLUMNS} rows={u.connections} renderRow={connectionRow} />
            </Panel>
            <Panel
              title="Latest 20 transfers"
              action={can("transfers.read") && <Link to={`${BASE}/transfers?username=${encodeURIComponent(username)}`} className={`text-xs ${linkCls}`}>All →</Link>}
            >
              <QueueTable columns={TRANSFER_COLUMNS} rows={u.transfers}
                renderRow={transferRow(can("transfers.read") ? setTransfer : null, false)} />
            </Panel>
            <Panel title="Latest 20 charges (as copier or master)">
              <QueueTable columns={chargeColumns(true)} rows={u.charges} renderRow={chargeRow(can("settlements.read") ? setCharge : null, true)} />
            </Panel>
          </div>
        )}
      </QueryState>
      <TransferDrawer id={transfer} onClose={() => setTransfer(null)} />
      <ChargeDrawer id={charge} onClose={() => setCharge(null)} />
      <CreateMasterDrawer key={username} open={creating} onClose={() => setCreating(false)} username={username} />
    </QueueLayout>
  );
}
